<template>
  <div class="chat-session-selector">
    <!-- Candidate Header -->
    <div class="candidate-header">
      <div class="candidate-info">
        <div class="candidate-avatar">
          {{ getInitials(selectedCandidate.name) }}
        </div>
        <div class="candidate-details">
          <h3>{{ selectedCandidate.name }}</h3>
          <p v-if="selectedCandidate.company" class="candidate-company">{{ selectedCandidate.company }}</p>
        </div>
      </div>
      <button class="create-analysis-pack-btn" @click="createCompanyAnalysisPack">
        <i class="las la-file-alt"></i>
        Create Company Analysis Pack
      </button>
    </div>

    <!-- Recent Files Section -->
    <div class="recent-files-section">
      <div class="sessions-header">
        <h4>Recent Files</h4>
        <button class="view-all-files-btn" @click="viewFiles">
          <i class="las la-folder-open"></i>
          View All Files
        </button>
      </div>
      
      <div v-if="filesLoading" class="loading">Loading files...</div>
      <div v-else-if="filesError" class="error-message">{{ filesError }}</div>
      <div v-else-if="recentFiles.length === 0" class="no-files">
        <div class="empty-state">
          <i class="las la-file"></i>
          <h5>No Files Yet</h5>
          <p>Upload files to start analyzing this candidate's documents. <router-link :to="`/manage/chatbot/candidate/${selectedCandidate.id}/upload`"><span class="upload-files-link">Upload Files <i class="las la-arrow-right"></i></span></router-link></p>
        </div>
      </div>
      <div v-else class="files-table-wrapper">
        <table class="files-table">
          <thead>
            <tr>
              <th class="col-icon"></th>
              <th class="col-name">File Name</th>
              <th class="col-type">Type</th>
              <th class="col-date">Uploaded</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in recentFiles" 
                :key="file.id || file.file_id || file.name" 
                class="file-row" 
                @click="viewFileDetails(file)">
              <td class="col-icon">
                <i :class="getFileIcon(file)"></i>
              </td>
              <td class="col-name">
                <span class="file-name">{{ formatFileName(file.name) }}</span>
              </td>
              <td class="col-type">
                <span class="file-type-badge">{{ formatFileType(file.type) }}</span>
              </td>
              <td class="col-date">
                <span class="file-date">{{ formatDate(file.scan_completed_at) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Chat Sessions Section -->
    <div class="sessions-section">
      <div class="sessions-header">
        <h4>Chat Sessions</h4>
        <button class="new-chat-btn" @click="createNewChat">
          <i class="las la-plus"></i>
          New Chat
        </button>
      </div>
      
      <div v-if="loading" class="loading">Loading chat sessions...</div>
      <div v-else-if="error" class="error-message">{{ error }}</div>
      <div v-else-if="chatSessions.length === 0" class="no-sessions">
        <div class="empty-state">
          <i class="las la-comments"></i>
          <h5>No Chat Sessions Yet</h5>
          <p>Start your first conversation with this candidate by clicking "New Chat" above.</p>
        </div>
      </div>
      <div v-else class="sessions-list">
        <div v-for="session in chatSessions" :key="session.id" 
             class="session-item" @click="selectSession(session)">
          <div class="session-icon">
            <i class="las la-comments"></i>
          </div>
          <div class="session-info">
            <div class="session-title">
              {{ session.chat_header || `Chat Session ${session.id}` }}
            </div>
          </div>
          <div class="session-actions">
            <button class="continue-btn" @click.stop="selectSession(session)">
              <i class="las la-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    
    <!-- Company Analysis Pack Modal -->
    <AnalysisPackModal
      v-if="selectedCandidate && selectedCandidate.id"
      ref="analysisPackModal"
      :candidate-id="selectedCandidate.id"
      @generated="onAnalysisPackGenerated"
    />
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import AnalysisPackModal from "./AnalysisPackModal.vue";

export default {
  name: 'ChatSessionSelector',
  components: {
    AnalysisPackModal
  },
  props: {
    selectedCandidate: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      chatSessions: [],
      loading: false,
      error: null,
      recentFiles: [],
      filesLoading: false,
      filesError: null
    }
  },
  async mounted() {
    await this.loadChatSessions();
    await this.loadRecentFiles();
  },
  methods: {
    async loadChatSessions() {
      if (!this.selectedCandidate || !this.selectedCandidate.id) {
        this.error = 'No candidate selected';
        return;
      }
      
      this.loading = true;
      try {
        const sessions = await ChatbotService.getChatSessions(this.selectedCandidate.id);
        this.chatSessions = Array.isArray(sessions) ? sessions : (sessions.sessions || sessions.data || []);
      } catch (err) {
        this.error = 'Failed to load chat sessions.';
        console.error('Error loading chat sessions:', err);
      } finally {
        this.loading = false;
      }
    },
    
    selectSession(session) {
      // Navigate to chat session page with multiple file support
      this.$router.push({ 
        name: 'chatbot-chat-session', 
        params: { 
          candidateId: this.selectedCandidate.id,
          sessionId: session.id
        }
      });
    },
    
    async createNewChat() {
      // Create new chat session and navigate to it
      console.log('createNewChat', this.selectedCandidate.id);
      try {
        const payload = {
          candidate_id: this.selectedCandidate.id,
        };
        
        const session = await ChatbotService.createChatSession(this.selectedCandidate.id, payload);
        
        this.$router.push({ 
          name: 'chatbot-chat-session', 
          params: { 
            candidateId: this.selectedCandidate.id,
            sessionId: session.id || session.session_id
          }
        });
      } catch (err) {
        console.error('Error creating chat session:', err);
        // Show error message to user instead of redirecting to non-existent route
        this.$bvToast.toast('Failed to create chat session. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      }
    },
    
    goToUpload() {
      this.$router.push({ 
        name: 'chatbot-upload',
        params: { 
          candidateId: this.selectedCandidate.id
        }
      });
    },
    
    viewFiles() {
      // Navigate to candidate files view
      this.$router.push({ 
        name: 'chatbot-candidate-files', 
        params: { 
          candidateId: this.selectedCandidate.id
        }
      });
    },
    
    async createCompanyAnalysisPack() {
      if (!this.selectedCandidate || !this.selectedCandidate.id) {
        this.$bvToast.toast('No candidate selected.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }
      
      // Wait for component to be available (v-if might need a tick)
      await this.$nextTick();
      
      // Open modal
      if (this.$refs.analysisPackModal && typeof this.$refs.analysisPackModal.show === 'function') {
        this.$refs.analysisPackModal.show();
      } else {
        console.error('AnalysisPackModal ref not available');
      }
    },
    
    onAnalysisPackGenerated() {
      // Optional: Handle successful generation
      // Could refresh file list or perform other actions
    },
    
    goBackToLanding() {
      this.$router.push({ name: 'chatbot' });
    },
    
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Today';
        if (diffDays === 2) return 'Yesterday';
        if (diffDays <= 7) return `${diffDays - 1} days ago`;
        
        return date.toLocaleDateString();
      } catch (e) {
        return dateString;
      }
    },
    
    async loadRecentFiles() {
      if (!this.selectedCandidate || !this.selectedCandidate.id) {
        this.filesError = 'No candidate selected';
        return;
      }
      
      this.filesLoading = true;
      this.filesError = null;
      
      try {
        const filesData = await ChatbotService.getUploads(this.selectedCandidate.id, { page: 1, page_size: 5 });
        this.recentFiles = Array.isArray(filesData) ? filesData : (filesData.files || filesData.uploads || filesData.data || []);
      } catch (error) {
        console.error('Error loading recent files:', error);
        this.filesError = 'Failed to load files';
      } finally {
        this.filesLoading = false;
      }
    },
    
    getFileIcon(file) {
      const documentType = file.document_type || file.type;
      
      switch (documentType) {
        case 'application/pdf':
        case 'pdf':
          return 'las la-file-pdf';
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'excel':
        case 'xlsx':
        case 'xls':
          return 'las la-file-excel';
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'word':
        case 'doc':
        case 'docx':
          return 'las la-file-word';
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'powerpoint':
        case 'ppt':
        case 'pptx':
          return 'las la-file-powerpoint';
        case 'text/csv':
        case 'csv':
          return 'las la-file-csv';
        case 'application/json':
        case 'json':
          return 'las la-file-code';
        case 'image/jpeg':
        case 'image/png':
        case 'image/gif':
        case 'image':
          return 'las la-file-image';
        default:
          return 'las la-file-alt';
      }
    },
    
    formatFileName(name) {
      if (!name) return 'Unknown File';
      return name.split('.')[0];
    },
    
    formatFileType(type) {
      switch (type) {
        case 'bank_statement':
          return 'Bank Statement';
        case 'emirates_id':
          return 'Emirates ID';
        case 'trade_license':
          return 'Trade License';
        case 'vat_statement':
          return 'VAT Statement';
        case 'aecb_report':
          return 'AECB Report';
        case 'payer_list':
          return 'Payer List';
        case 'credit_model':
          return 'Credit Model';
        default:
          return type || 'Unknown';
      }
    },
    
    viewFileDetails(file) {
      const fileId = file.id || file.file_id;
      if (fileId) {
        this.$router.push({
          name: 'chatbot-file-details',
          params: {
            candidateId: this.selectedCandidate.id,
            fileId: fileId
          }
        });
      }
    },
    
    truncateMessage(message, maxLength = 60) {
      if (!message) return '';
      if (message.length <= maxLength) return message;
      return message.substring(0, maxLength) + '...';
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.chat-session-selector {
  width: 100%;
  height: 100%;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
}

.candidate-header {
  padding: 2rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.candidate-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.candidate-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, $color-primary, darken($color-primary, 15%));
  color: $color-secondary;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.candidate-details h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
  line-height: 1.2;
}

.candidate-company {
  margin: 0;
  font-size: 1rem;
  color: #718096;
  font-family: $brand-font-regular;
}

.sessions-section {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  background: #ffffff;
}

.sessions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h4 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: #2d3748;
    font-family: $brand-font-medium;
  }
}

.new-chat-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: darken($color-primary, 10%);
  }
  
  i {
    font-size: 0.9rem;
    color: $color-secondary;
  }
}

.loading, .no-sessions {
  text-align: center;
  color: #718096;
  padding: 3rem 2rem;
  font-style: italic;
  font-family: $brand-font-regular;
}

.error-message {
  text-align: center;
  color: #e53e3e;
  padding: 1rem 2rem;
  font-size: 0.9rem;
  font-family: $brand-font-regular;
  background: #fed7d7;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  margin: 1rem 0;
}

.empty-state {
  text-align: center;
  
  i {
    font-size: 4rem;
    color: #a0aec0;
    margin-bottom: 1.5rem;
    display: block;
  }
  
  h5 {
    margin: 0 0 0.75rem 0;
    color: #2d3748;
    font-size: 1.2rem;
  }
  
  p {
    margin: 0;
    color: #718096;
    font-size: 1rem;
  }
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.session-item {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  
  &:hover {
    background: #edf2f7;
    border-color: $color-primary;
  }
}

.session-icon {
  color: $color-primary;
  font-size: 1.75rem;
  flex-shrink: 0;
}

.session-info {
  flex: 1;
  min-width: 0;
}

.session-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.session-actions {
  flex-shrink: 0;
}

.continue-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 1rem 1rem;
  border-radius: 50%;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: darken($color-primary, 10%);
  }
  
  i {
    font-size: 0.9rem;
  }
}

// Responsive design
@media (max-width: 768px) {
  .chat-session-selector {
    margin: 0;
  }
  
  .candidate-header {
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    
    .create-analysis-pack-btn {
      width: 100%;
      justify-content: center;
      padding: 0.875rem 1.25rem;
      font-size: 0.9rem;
      
      i {
        font-size: 1rem;
      }
    }
  }
  
  .sessions-section {
    padding: 1.5rem;
  }
  
  .candidate-info {
    gap: 1rem;
  }
  
  .candidate-avatar {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  
  .candidate-details h3 {
    font-size: 1.3rem;
  }
}

// Company Analysis Pack Section
.create-analysis-pack-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: $color-primary;
  color: $color-secondary;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: $brand-font-medium;
  
  &:hover {
    background: darken($color-primary, 10%);
  }
  
  i {
    font-size: 1.1rem;
    color: $color-secondary;
  }
}

// Recent Files Section
.recent-files-section {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
}

.view-all-files-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: $color-primary;
  color: $color-secondary;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: darken($color-primary, 10%);
  }
  
  i {
    font-size: 0.875rem;
    color: $color-secondary;
  }
}

.files-table-wrapper {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.files-table {
  width: 100%;
  border-collapse: collapse;
  
  thead {
    background: #f8f9fa;
    border-bottom: 2px solid #e2e8f0;
    
    th {
      padding: 0.875rem 1rem;
      text-align: left;
      font-size: 0.75rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-family: $brand-font-medium;
      
      &.col-icon {
        width: 50px;
        text-align: center;
      }
      
      &.col-name {
        width: auto;
      }
      
      &.col-type {
        width: 180px;
      }
      
      &.col-date {
        width: 150px;
      }
    }
  }
  
  tbody {
    .file-row {
      cursor: pointer;
      border-bottom: 1px solid #f1f3f5;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      td {
        padding: 1rem;
        vertical-align: middle;
        
        &.col-icon {
          text-align: center;
          
          i {
            font-size: 1.5rem;
            color: $color-primary;
            opacity: 0.8;
          }
        }
        
        &.col-name {
          .file-name {
            color: #2d3748;
            font-size: 0.95rem;
            font-weight: 500;
            font-family: $brand-font-medium;
          }
        }
        
        &.col-type {
          .file-type-badge {
            display: inline-block;
            padding: 0.25rem 0.625rem;
            background: #e9ecef;
            color: #495057;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-family: $brand-font-medium;
          }
        }
        
        &.col-date {
          .file-date {
            color: #6c757d;
            font-size: 0.875rem;
            font-family: $brand-font-regular;
          }
        }
      }
    }
  }
}

.no-files {
  text-align: center;
  padding: 2rem;
  
  .empty-state {
    i {
      font-size: 2.5rem;
      color: #adb5bd;
      margin-bottom: 1rem;
    }
    
    h5 {
      color: #6c757d;
      margin: 0 0 0.5rem 0;
      font-size: 1rem;
    }
    
    p {
      color: #adb5bd;
      margin: 0;
      font-size: 0.875rem;
      
      :deep(a) {
        color: $color-primary;
        text-decoration: none;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.upload-files-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  color: $color-primary !important;

  
  i {
    display: inline;
    font-size: 0.875rem !important;
    margin-bottom: 0 !important;
    
  }
  &:hover { 
      color: darken($color-primary, 30%) !important;
  }
}


// Responsive design
@media (max-width: 768px) {
  .recent-files-section {
    padding: 1rem;
  }
  
  .files-table-wrapper {
    overflow-x: auto;
  }
  
  .files-table {
    thead th {
      padding: 0.75rem 0.75rem;
      font-size: 0.7rem;
      
      &.col-type {
        width: 150px;
      }
      
      &.col-date {
        width: 120px;
      }
    }
    
    tbody .file-row td {
      padding: 0.875rem 0.75rem;
      
      &.col-icon i {
        font-size: 1.25rem;
      }
      
      &.col-name .file-name {
        font-size: 0.875rem;
      }
      
      &.col-type .file-type-badge {
        font-size: 0.7rem;
        padding: 0.2rem 0.5rem;
      }
      
      &.col-date .file-date {
        font-size: 0.8rem;
      }
    }
  }
}

</style> 